/**
 * Read-only fetch for admin Excel export.
 * Requires Firestore rules that allow the signed-in admin to list/read these collections
 * (e.g. via custom claims), or a backend proxy - do not expose participant data to all users.
 */
import { collection, getDocs, query, where, Timestamp, getDoc, doc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db, isFirebaseConfigured } from '../firebase';
import { isRowExcludedFromDataExport } from '../config/dataExportAdmins';
import { resolveExportUserRole } from '../config/exportUserRoles';
import { DEFAULT_EXPORT_FROM_YMD, documentCreatedAtToMillis, localYmdToStartDate } from '../utils/exportDateFilter';

export { DEFAULT_EXPORT_FROM_YMD } from '../utils/exportDateFilter';

export const REGISTERED_USERS_SHEET_TITLE = 'Registered users';

export const FEEDBACK_EXPORT_COLLECTIONS = [
  { id: 'research_consent_supplements', sheetTitle: 'Research consent' },
  { id: 'feedback', sheetTitle: 'Website feedback' },
  { id: 'workplace_module_surveys', sheetTitle: 'Workplace surveys' },
  { id: 'workplace_module_participant_reviews', sheetTitle: 'Participant reviews' },
  { id: 'parent_module_responses', sheetTitle: 'Parent responses' },
  { id: 'reflection_submissions', sheetTitle: 'Reflection submissions' },
  { id: 'intentional_shift_commitments', sheetTitle: 'Intentional shifts' },
  { id: 'educator_barrier_recognition_responses', sheetTitle: 'Barrier recognition' },
  { id: 'meta_reflexivity_responses', sheetTitle: 'Meta reflexivity' },
];

/** Sheet order in the downloaded workbook (first sheet = user directory). */
export const FEEDBACK_EXPORT_WORKBOOK_SHEETS = [
  REGISTERED_USERS_SHEET_TITLE,
  ...FEEDBACK_EXPORT_COLLECTIONS.map((c) => c.sheetTitle),
];

/**
 * @param {string[]} uids
 * @returns {Promise<Record<string, { exportAuthEmail: string, exportAuthDisplayName: string, authCreatedAt: unknown, exportAuthSyncedAt: unknown }>>}
 */
async function fetchUserExportProfileMap(uids) {
  if (!db) return {};
  const uniq = [...new Set(uids.map((u) => String(u).trim()).filter(Boolean))];
  /** @type {Record<string, { exportAuthEmail: string, exportAuthDisplayName: string, authCreatedAt: unknown, exportAuthSyncedAt: unknown }>} */
  const map = {};
  await Promise.all(
    uniq.map(async (uid) => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (!snap.exists()) {
          map[uid] = { exportAuthEmail: '', exportAuthDisplayName: '', authCreatedAt: '', exportAuthSyncedAt: null };
          return;
        }
        const data = snap.data();
        map[uid] = {
          exportAuthEmail: data.exportAuthEmail != null ? String(data.exportAuthEmail) : '',
          exportAuthDisplayName: data.exportAuthDisplayName != null ? String(data.exportAuthDisplayName) : '',
          authCreatedAt: data.authCreatedAt ?? '',
          exportAuthSyncedAt: data.exportAuthSyncedAt ?? null,
        };
      } catch {
        map[uid] = { exportAuthEmail: '', exportAuthDisplayName: '', authCreatedAt: '', exportAuthSyncedAt: null };
      }
    })
  );
  return map;
}

/**
 * @param {Record<string, unknown>} row
 * @param {Record<string, { exportAuthEmail: string, exportAuthDisplayName: string, authCreatedAt?: unknown, exportAuthSyncedAt?: unknown }>} profileMap
 */
function enrichExportRow(row, profileMap) {
  const uid = row.userId != null ? String(row.userId).trim() : '';
  const emailFromDoc = row.email != null ? String(row.email).trim() : '';
  const prof = uid && profileMap[uid] ? profileMap[uid] : null;
  const email = emailFromDoc || (prof?.exportAuthEmail ?? '') || '';
  const displayName = (prof?.exportAuthDisplayName ?? '') || '';
  return {
    participantEmail: email,
    participantDisplayName: displayName,
    ...row,
    userRole: resolveExportUserRole(email, {
      authCreatedAt: row.authCreatedAt ?? prof?.authCreatedAt,
      exportAuthSyncedAt: row.exportAuthSyncedAt ?? prof?.exportAuthSyncedAt,
    }),
  };
}

function withExportUserRole(row) {
  const email =
    (row.participantEmail != null ? String(row.participantEmail).trim() : '') ||
    (row.email != null ? String(row.email).trim() : '');
  return {
    ...row,
    userRole: resolveExportUserRole(email, row),
  };
}

function sortRegisteredUserRows(rows) {
  return [...rows].sort((a, b) => {
    const ea = String(a.participantEmail || '').toLowerCase();
    const eb = String(b.participantEmail || '').toLowerCase();
    if (ea && eb) return ea.localeCompare(eb);
    if (ea) return -1;
    if (eb) return 1;
    return 0;
  });
}

function mapFirestoreUserDoc(d) {
  const data = d.data();
  const enrolled = Array.isArray(data.enrolledModules) ? data.enrolledModules : [];
  const completed = Array.isArray(data.completedModules) ? data.completedModules : [];
  return {
    participantEmail: data.exportAuthEmail != null ? String(data.exportAuthEmail) : '',
    participantDisplayName:
      data.exportAuthDisplayName != null ? String(data.exportAuthDisplayName) : '',
    authCreatedAt: data.authCreatedAt != null ? String(data.authCreatedAt) : '',
    authLastSignInAt: data.authLastSignInAt != null ? String(data.authLastSignInAt) : '',
    exportAuthSyncedAt: data.exportAuthSyncedAt ?? null,
    enrolledModules: enrolled.map(String).join('; '),
    completedModules: completed.map(String).join('; '),
    updatedAt: data.updatedAt ?? null,
    profileSource: 'firestore',
  };
}

/** Full Firebase Auth list (requires deployed `listUsersForExport` Cloud Function). */
async function fetchRegisteredUsersFromAuth() {
  if (!app) return null;
  try {
    const listUsers = httpsCallable(getFunctions(app), 'listUsersForExport');
    const result = await listUsers();
    const users = result?.data?.users;
    if (!Array.isArray(users)) return null;
    return users;
  } catch (e) {
    console.warn('[feedbackExportData] listUsersForExport', e);
    return null;
  }
}

/**
 * Firestore-only fallback: `users` collection plus any `userId` seen in submission collections.
 * Does not include Auth accounts that never opened the app and never submitted data.
 */
async function fetchRegisteredUsersFromFirestore() {
  /** @type {Map<string, Record<string, unknown>>} */
  const byUid = new Map();

  const usersSnap = await getDocs(collection(db, 'users'));
  usersSnap.docs.forEach((d) => {
    byUid.set(d.id, mapFirestoreUserDoc(d));
  });

  const orphanUids = new Set();
  await Promise.all(
    FEEDBACK_EXPORT_COLLECTIONS.map(async ({ id }) => {
      const snap = await getDocs(collection(db, id));
      snap.docs.forEach((d) => {
        const uid = d.data().userId != null ? String(d.data().userId).trim() : '';
        if (uid && !byUid.has(uid)) orphanUids.add(uid);
      });
    })
  );

  if (orphanUids.size) {
    const profileMap = await fetchUserExportProfileMap([...orphanUids]);
    for (const uid of orphanUids) {
      const prof = profileMap[uid] || {
        exportAuthEmail: '',
        exportAuthDisplayName: '',
        authCreatedAt: '',
        exportAuthSyncedAt: null,
      };
      byUid.set(uid, {
        participantEmail: prof.exportAuthEmail,
        participantDisplayName: prof.exportAuthDisplayName,
        authCreatedAt: prof.authCreatedAt ?? '',
        authLastSignInAt: '',
        exportAuthSyncedAt: prof.exportAuthSyncedAt ?? null,
        enrolledModules: '',
        completedModules: '',
        updatedAt: null,
        profileSource: 'submissions_only',
      });
    }
  }

  return [...byUid.values()];
}

async function fetchRegisteredUsersRows() {
  const fromAuth = await fetchRegisteredUsersFromAuth();
  const rows = fromAuth?.length ? fromAuth : await fetchRegisteredUsersFromFirestore();
  return sortRegisteredUserRows(
    rows.filter((row) => !isRowExcludedFromDataExport(row)).map(withExportUserRole)
  );
}

/**
 * @param {{ exportFromYmd?: string }} [options] - `exportFromYmd` is `YYYY-MM-DD` (local); inclusive from start of that day.
 * @returns {Promise<Record<string, Array<Record<string, unknown>>>>}
 */
export async function fetchFeedbackExportPayload(options = {}) {
  if (!db || !isFirebaseConfigured()) {
    throw new Error('Firebase is not configured.');
  }
  const ymd = String(options.exportFromYmd || '').trim() || DEFAULT_EXPORT_FROM_YMD;
  const fromDate = localYmdToStartDate(ymd);
  const fromMillis = fromDate.getTime();
  const tsLowerBound = Timestamp.fromDate(fromDate);
  const isoLowerBound = fromDate.toISOString();

  /** @type {Record<string, Array<Record<string, unknown>>>} */
  const out = {};
  await Promise.all(
    FEEDBACK_EXPORT_COLLECTIONS.map(async ({ id, sheetTitle }) => {
      const colRef = collection(db, id);
      const q =
        id === 'feedback'
          ? query(colRef, where('createdAt', '>=', isoLowerBound))
          : query(colRef, where('createdAt', '>=', tsLowerBound));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => d.data())
        .filter((row) => {
          const ms = documentCreatedAtToMillis(row);
          return ms != null && ms >= fromMillis;
        });
      out[sheetTitle] = rows;
    })
  );

  const allUids = [];
  for (const { sheetTitle } of FEEDBACK_EXPORT_COLLECTIONS) {
    for (const row of out[sheetTitle] || []) {
      if (row.userId) allUids.push(String(row.userId));
    }
  }
  const profileMap = await fetchUserExportProfileMap(allUids);
  for (const { sheetTitle } of FEEDBACK_EXPORT_COLLECTIONS) {
    out[sheetTitle] = (out[sheetTitle] || [])
      .map((row) => enrichExportRow(row, profileMap))
      .filter((row) => !isRowExcludedFromDataExport(row));
  }

  out[REGISTERED_USERS_SHEET_TITLE] = await fetchRegisteredUsersRows();

  return out;
}
